import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, saveUserProfile, updateAddresses } from '../services/userService';

export default function EditProfile() {
  const { userInfo, getIdToken } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');

  const [formData, setFormData] = useState({
    firstName: userInfo?.displayName?.split(' ')[0] || '',
    lastName: userInfo?.displayName?.split(' ').slice(1).join(' ') || '',
    email: userInfo?.email || '',
    phone: '',
    dateOfBirth: '',
  });

  const [addresses, setAddresses] = useState([]);

  // Load profile from MongoDB backend on mount
  useEffect(() => {
    if (!userInfo?.uid) return;
    (async () => {
      try {
        const idToken = await getIdToken();
        const profile = await getUserProfile(userInfo.uid, idToken);
        if (!profile) return;
        setFormData((prev) => ({
          ...prev,
          firstName: profile.firstName || prev.firstName,
          lastName: profile.lastName || prev.lastName,
          phone: profile.phone || '',
          dateOfBirth: profile.dateOfBirth || '',
        }));
        if (profile.addresses) setAddresses(profile.addresses);
      } catch (err) {
        console.warn('Failed to load profile:', err.message);
      }
    })();
  }, [userInfo?.uid, getIdToken]);

  const [newAddress, setNewAddress] = useState({
    label: '',
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false,
  });

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleAddressChange = e => {
    const { name, value } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePersonalInfo = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Enter valid 10-digit phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAddress = () => {
    const newErrors = {};
    if (!newAddress.label.trim()) newErrors.label = 'Address label is required';
    if (!newAddress.name.trim()) newErrors.name = 'Name is required';
    if (!newAddress.phone.trim()) newErrors.phone = 'Phone is required';
    if (!/^\d{10}$/.test(newAddress.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Enter valid 10-digit phone';
    }
    if (!newAddress.address.trim()) newErrors.address = 'Address is required';
    if (!newAddress.city.trim()) newErrors.city = 'City is required';
    if (!newAddress.state.trim()) newErrors.state = 'State is required';
    if (!/^\d{6}$/.test(newAddress.pincode)) newErrors.pincode = 'Enter valid 6-digit pincode';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSavePersonal = async () => {
    if (validatePersonalInfo()) {
      try {
        const idToken = await getIdToken();
        await saveUserProfile(userInfo.uid, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          dateOfBirth: formData.dateOfBirth,
          email: formData.email,
        }, idToken);
        setSuccessMessage('Personal information updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Failed to save profile:', err);
        setSuccessMessage('Failed to save. Please try again.');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    }
  };

  const handleAddAddress = async () => {
    if (validateAddress()) {
      const newAddr = { ...newAddress, id: Date.now() };
      const updatedAddresses = [...addresses, newAddr];
      try {
        const idToken = await getIdToken();
        await updateAddresses(userInfo.uid, updatedAddresses, idToken);
        setAddresses(updatedAddresses);
        setNewAddress({ label: '', name: '', phone: '', address: '', city: '', state: '', pincode: '', isDefault: false });
        setShowAddressForm(false);
        setSuccessMessage('Address added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Failed to add address:', err);
      }
    }
  };

  const handleDeleteAddress = async (id) => {
    const updatedAddresses = addresses.filter(addr => addr.id !== id);
    try {
      const idToken = await getIdToken();
      await updateAddresses(userInfo.uid, updatedAddresses, idToken);
      setAddresses(updatedAddresses);
      setSuccessMessage('Address removed');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Failed to delete address:', err);
    }
  };

  const handleSetDefault = id => {
    setAddresses(
      addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === id,
      }))
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20">
      <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-8">Edit Profile</h1>

      {successMessage && (
        <div className="mb-6 p-4 bg-green-100 border-l-4 border-green-500 rounded text-green-800 text-sm">
          ✓ {successMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-gray-300 overflow-x-auto scrollbar-hide">
        <button
          onClick={() => setActiveTab('personal')}
          className={`px-4 py-3 font-semibold border-b-2 transition whitespace-nowrap ${
            activeTab === 'personal'
              ? 'border-secondary text-secondary'
              : 'border-transparent text-gray-600 hover:text-bodyText'
          }`}
        >
          Personal Info
        </button>
        <button
          onClick={() => setActiveTab('addresses')}
          className={`px-4 py-3 font-semibold border-b-2 transition whitespace-nowrap ${
            activeTab === 'addresses'
              ? 'border-secondary text-secondary'
              : 'border-transparent text-gray-600 hover:text-bodyText'
          }`}
        >
          Addresses
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-3 font-semibold border-b-2 transition whitespace-nowrap ${
            activeTab === 'settings'
              ? 'border-secondary text-secondary'
              : 'border-transparent text-gray-600 hover:text-bodyText'
          }`}
        >
          Settings
        </button>
      </div>

      {/* Personal Information Tab */}
      {activeTab === 'personal' && (
        <div className="bg-cardBg rounded-lg p-6 md:p-8 shadow-lg max-w-2xl">
          <h2 className="text-2xl font-heading text-headingText mb-6">Personal Information</h2>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-bodyText mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-bodyText mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-bodyText mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 bg-gray-100 text-bodyText cursor-not-allowed"
              />
              <p className="text-xs text-gray-600 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-bodyText mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="10-digit phone number"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-bodyText mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
              />
            </div>

            <button
              onClick={handleSavePersonal}
              className="w-full px-6 py-3 rounded-lg bg-secondary text-primaryBg font-semibold hover:opacity-90 transition"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Addresses Tab */}
      {activeTab === 'addresses' && (
        <div className="space-y-6">
          {/* Saved Addresses */}
          <div className="space-y-4">
            {addresses.map(addr => (
              <div key={addr.id} className="bg-cardBg rounded-lg p-6 shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-semibold text-bodyText mb-1">
                      {addr.label} {addr.isDefault && <span className="text-xs text-secondary">DEFAULT</span>}
                    </p>
                    <p className="text-sm text-bodyText">{addr.name}</p>
                    <p className="text-sm text-bodyText">{addr.phone}</p>
                  </div>
                  <div className="flex gap-2">
                    {!addr.isDefault && (
                      <button
                        onClick={() => handleSetDefault(addr.id)}
                        className="text-sm px-3 py-1 rounded border border-secondary text-secondary hover:bg-secondary hover:text-primaryBg transition"
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteAddress(addr.id)}
                      className="text-sm px-3 py-1 rounded border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="text-sm text-bodyText">
                  {addr.address}, {addr.city}, {addr.state} - {addr.pincode}
                </p>
              </div>
            ))}
          </div>

          {/* Add New Address */}
          {!showAddressForm && (
            <button
              onClick={() => setShowAddressForm(true)}
              className="w-full px-6 py-3 rounded-lg border-2 border-secondary text-secondary font-semibold hover:bg-secondary hover:text-primaryBg transition"
            >
              + Add New Address
            </button>
          )}

          {showAddressForm && (
            <div className="bg-cardBg rounded-lg p-6 shadow-lg">
              <h3 className="text-xl font-heading text-headingText mb-6">Add New Address</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-bodyText mb-2">Label</label>
                  <input
                    type="text"
                    name="label"
                    placeholder="e.g., Home, Office, Other"
                    value={newAddress.label}
                    onChange={handleAddressChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                      errors.label ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.label && <p className="text-red-500 text-sm mt-1">{errors.label}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-bodyText mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={newAddress.name}
                      onChange={handleAddressChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-bodyText mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={newAddress.phone}
                      onChange={handleAddressChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-bodyText mb-2">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={newAddress.address}
                    onChange={handleAddressChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                      errors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-bodyText mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={newAddress.city}
                      onChange={handleAddressChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                        errors.city ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-bodyText mb-2">State</label>
                    <input
                      type="text"
                      name="state"
                      value={newAddress.state}
                      onChange={handleAddressChange}
                      className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                        errors.state ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-bodyText mb-2">Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={newAddress.pincode}
                      onChange={handleAddressChange}
                      maxLength="6"
                      className={`w-full px-4 py-3 rounded-lg border-2 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary ${
                        errors.pincode ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleAddAddress}
                    className="flex-1 px-6 py-3 rounded-lg bg-secondary text-primaryBg font-semibold hover:opacity-90 transition"
                  >
                    Add Address
                  </button>
                  <button
                    onClick={() => setShowAddressForm(false)}
                    className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-300 text-bodyText font-semibold hover:border-secondary transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-cardBg rounded-lg p-6 md:p-8 shadow-lg max-w-2xl">
          <h2 className="text-2xl font-heading text-headingText mb-6">Account Settings</h2>

          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg hover:bg-primaryBg transition">
                <input type="checkbox" defaultChecked className="w-5 h-5 accent-secondary" />
                <div>
                  <p className="font-semibold text-bodyText">Email Notifications</p>
                  <p className="text-sm text-gray-600">Get updates about orders and promotions</p>
                </div>
              </label>
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg hover:bg-primaryBg transition">
                <input type="checkbox" defaultChecked className="w-5 h-5 accent-secondary" />
                <div>
                  <p className="font-semibold text-bodyText">SMS Notifications</p>
                  <p className="text-sm text-gray-600">Get SMS updates for order delivery</p>
                </div>
              </label>
            </div>

            <div className="border-t border-gray-300 pt-6">
              <p className="font-semibold text-bodyText mb-4">Security</p>
              <button className="w-full px-6 py-3 rounded-lg border-2 border-secondary text-secondary font-semibold hover:bg-secondary hover:text-primaryBg transition">
                Change Password
              </button>
            </div>

            <div className="border-t border-gray-300 pt-6">
              <p className="font-semibold text-bodyText mb-2 text-red-600">Danger Zone</p>
              <button className="w-full px-6 py-3 rounded-lg border-2 border-red-500 text-red-500 font-semibold hover:bg-red-500 hover:text-white transition">
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
